import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import _throttle from 'lodash/throttle';
import {
  CamPlayer,
  GoogleDFP,
  PersistentMediaPlayer,
  ClosedMediaPlayer,
  PremiumCamCTA,
} from '@surfline/quiver-react';
import isEqual from 'lodash/isEqual';
import getSubscriptionEntitlement from '../../utils/getSubscriptionEntitlement';
import { readStickyCamCookie, setStickyCamCookie } from '../../utils/spotCamCookie';
import config from '../../config';
import CTAProvider from '../../containers/CTAProvider';
import { clickedSubscribeCTA, trackEvent } from '../../actions/segmentEvents';
import { getUserSettings } from '../../selectors/user';
import {
  getAdvertisingIds,
  getNearbySpots,
  getReverseSortBeachView,
  getSpotReportAssociated,
} from '../../selectors/spot';
import { getCamListScrollPosition } from '../../selectors/animations';
import spotReportDataPropType from '../../propTypes/spot';
import userDetailsPropTypes from '../../propTypes/userDetails';
import userSettingsPropTypes from '../../propTypes/userSettings';
import cameraPropType from '../../propTypes/camera';
import loadAdConfig from '../../utils/adConfig';
import getUTCDate from '../../utils/getUTCDate';
import getCamStillImage from '../../utils/getCamStillImage';
import SpotCamOverlay from '../SpotCamOverlay';

import SpotReportCamStyles from '../../styles/modules/SpotReportCamModules.scss';

class SpotReportCam extends Component {
  constructor(props) {
    super(props);
    this.state = {
      advertisingIds: props.advertisingIds,
      stickyCamIsActive: false,
      stickyCamIsOpen: true,
      camRefHeight: null,
      camPlayerAd: {
        height: 0,
        width: 0,
      },
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('scroll', this.scrollListen);
  }

  componentDidUpdate(prevProps) {
    const { advertisingIds } = this.props;
    if (!isEqual(prevProps.advertisingIds, advertisingIds)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ advertisingIds });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('scroll', this.scrollListen);
  }

  // eslint-disable-next-line react/sort-comp
  resizeListener = _throttle(() => {
    this.setCamRefHeight();
  }, 350);

  // eslint-disable-next-line react/sort-comp
  scrollListen = _throttle(() => {
    this.setCamRefHeight();
    this.setCamPlayerAdDimensions();
  }, 350);

  setCamRefHeight = () => {
    const { stickyCamIsActive, camRefHeight } = this.state;
    if (this.camPlayerRef && !stickyCamIsActive) {
      const { height } = this.camPlayerRef.getBoundingClientRect();
      if (camRefHeight !== height) {
        this.setState({ camRefHeight: height });
      }
    }
  };

  setCamPlayerAdDimensions = () => {
    const { playerId } = this.props;
    const { camPlayerAd, stickyCamIsOpen, stickyCamIsActive } = this.state;
    const { height, width } = this.camPlayerRef.getBoundingClientRect();
    const isDimensionsDiff = camPlayerAd.width !== width && camPlayerAd.height !== height;
    if (document && playerId && isDimensionsDiff) {
      const playerAd = document.getElementById(`${playerId}_ad`);
      playerAd.firstElementChild.style.height = `${height}px`;
      playerAd.firstElementChild.style.width = `${width}px`;
      const video = playerAd.querySelector('video');
      if (video) {
        video.style.height = `${height}px`;
        video.style.width = `${width}px`;
      }
      const iframe = playerAd.querySelector('iframe');
      if (iframe) {
        iframe.style.height = `${height}px`;
        iframe.style.width = `${width}px`;
      }
      const topLevelDivs = playerAd.querySelectorAll(':scope > div');
      if (topLevelDivs.length > 1) {
        topLevelDivs.forEach(div => {
          /* eslint-disable no-param-reassign */
          div.style.height = `${height}px`;
          div.style.width = `${width}px`;
        });
      }

      this.setState({ camPlayerAd: { height, width } });
    }
    // Fixes an issue where the persistent cam styles were breaking on Safari.
    // For some reason the inline style changes causes the CSS to break, so
    // overriding them specifically on the persistent cam last allows Safari to repaint
    // properly
    // https://wavetrak.atlassian.net/browse/KBYG-2561
    const persistentCam = document.querySelector('.sl-persistent-spot-report-cam');
    persistentCam.style.position = stickyCamIsActive && stickyCamIsOpen ? 'relative' : '';
  };

  getCamRewindUrl = () => {
    const { legacySpotId, camera, isPremium, isMultiCam } = this.props;
    if (isPremium) {
      const camRewindUrl = config.camRewindUrl(legacySpotId);
      if (isMultiCam && camera.alias) {
        return `${camRewindUrl}&camAlias=${camera.alias}`;
      }
      return camRewindUrl;
    }
    return config.camRewindUrlNonPremium;
  };

  getCamPlayerClasses = (stickyCamIsActive, stickyCamIsOpen) =>
    classNames({
      'sl-persistent-spot-report-cam': true,
      'sl-persistent-spot-report-cam--closed': stickyCamIsActive && !stickyCamIsOpen,
    });

  readInitialStickyCamCookie = () => {
    const stickyCamVisibleCookie = readStickyCamCookie();
    this.setState({ stickyCamIsOpen: stickyCamVisibleCookie });
  };

  toggleStickyCamPlayer = isSticky => {
    this.setState({
      stickyCamIsActive: isSticky,
      stickyCamIsOpen: readStickyCamCookie(),
    });
  };

  toggleStickyCamOpen = () => {
    const { isPremium, userDetails, doTrackEvent } = this.props;
    const { stickyCamIsOpen } = this.state;
    const subscriptionEntitlement = getSubscriptionEntitlement(isPremium, userDetails);
    doTrackEvent(`${stickyCamIsOpen ? 'Closed' : 'Opened'} Cam`, {
      subscriptionEntitlement,
    });
    setStickyCamCookie(!stickyCamIsOpen);
    this.setState({ stickyCamIsOpen: !stickyCamIsOpen });
  };

  render() {
    const {
      adblock,
      doClickedSubscribeCTA,
      isPremium,
      legacySpotId,
      reportData,
      spot,
      spotId,
      camera,
      userSettings,
      playerId,
      isMultiCam,
      isPrimaryCam,
      multiCamTimeout,
      timeoutMultiCams,
      resumeMultiCams,
      messageType,
      reportAssociated,
      adFreePopupText,
    } = this.props;
    const { utcOffset } = reportAssociated;
    const { name } = spot;
    const { advertisingIds, stickyCamIsActive, stickyCamIsOpen, camRefHeight } = this.state;

    const defaultSegmentProperties = {
      pageName: 'Spot Report',
      spotId,
      spotName: name,
    };

    const premiumCamCTASegmentProperties = {
      ctaLocation: 'premium only cam - spot',
      category: 'cams & reports',
      ...defaultSegmentProperties,
    };

    const camStillUrl = getCamStillImage(camera);
    const isPremiumCam = camera.isPremium;
    const showPremiumCamPaywall = isPremiumCam && !isPremium;
    if (showPremiumCamPaywall) {
      return (
        <CTAProvider segmentProperties={premiumCamCTASegmentProperties}>
          <PremiumCamCTA spotName={spot.name} camStillUrl={camStillUrl} />
        </CTAProvider>
      );
    }

    const adBlockSegmentProperties = {
      ctaLocation: 'cam player ad blocker',
      ...defaultSegmentProperties,
    };

    const midRollSegmentProperties = {
      ctaLocation: 'mid roll',
      ...defaultSegmentProperties,
    };

    const prerecordedTimeRange = camera.isPrerecorded &&
      camera.lastPrerecordedClipStartTimeUTC &&
      camera.lastPrerecordedClipEndTimeUTC && {
        start:
          getUTCDate(new Date(camera.lastPrerecordedClipStartTimeUTC)).getTime() +
          60 * 1000 * 60 * utcOffset,
        end:
          getUTCDate(new Date(camera.lastPrerecordedClipEndTimeUTC)).getTime() +
          60 * 1000 * 60 * utcOffset,
      };

    const CameraPlayer = isPersistentCam => (
      <CamPlayer
        adFreePopupText={adFreePopupText}
        cssModules={SpotReportCamStyles}
        prerecordedTimeRange={prerecordedTimeRange}
        playerId={playerId || 'sl-spot-report-camera'}
        aspectRatio="16:9"
        camera={camera}
        funnelUrl={config.funnelUrl}
        rewindUrl={this.getCamRewindUrl()}
        legacySpotId={legacySpotId}
        spotName={name}
        spotId={spotId}
        isPremium={isPremium}
        adblock={adblock && !isPremium}
        advertisingIds={advertisingIds}
        onClickAdBlock={() => doClickedSubscribeCTA(adBlockSegmentProperties)}
        onClickUpSell={() => doClickedSubscribeCTA(midRollSegmentProperties)}
        camHostAd={
          <div className="sl-spot-report-page__camera__host-ad">
            <GoogleDFP
              adConfig={{
                ...loadAdConfig('camHostAd', [
                  ['spotid', advertisingIds.spotId],
                  ['camId', camera._id],
                  ['className', 'kbyg-cam'],
                ]),
              }}
            />
          </div>
        }
        isMultiCam={isMultiCam}
        isPrimaryCam={isPrimaryCam}
        multiCamTimeout={multiCamTimeout}
        timeoutMultiCams={timeoutMultiCams}
        resumeMultiCams={resumeMultiCams}
        messageType={messageType}
        showOverlays={!isPersistentCam}
      />
    );

    return (
      <div className="sl-spot-report-page__camera-container">
        <div
          className="sl-spot-report-page__camera"
          style={{ height: camRefHeight }}
          ref={el => {
            this.camRef = el;
          }}
        >
          {isPrimaryCam || !isMultiCam ? (
            <div className={this.getCamPlayerClasses(stickyCamIsActive, stickyCamIsOpen)}>
              <PersistentMediaPlayer
                containerRef={this.camRef}
                stickyToggleCallback={isSticky => this.toggleStickyCamPlayer(isSticky)}
              >
                {(stickyCamIsActive && stickyCamIsOpen) || !stickyCamIsActive ? (
                  <div className="sl-persistent-spot-report-cam__overlay">
                    <div
                      ref={el => {
                        this.camPlayerRef = el;
                      }}
                    >
                      {stickyCamIsActive && (
                        <SpotCamOverlay
                          isPremium={isPremium}
                          onClickCloseHandler={this.toggleStickyCamOpen}
                          reportData={reportData}
                          spot={spot}
                          stickyCamIsOpen={stickyCamIsOpen}
                          userSettings={userSettings}
                        />
                      )}
                      {CameraPlayer(stickyCamIsActive)}
                    </div>
                  </div>
                ) : null}
                {stickyCamIsActive && !stickyCamIsOpen ? (
                  <ClosedMediaPlayer onClickHandler={this.toggleStickyCamOpen} />
                ) : null}
              </PersistentMediaPlayer>
            </div>
          ) : (
            CameraPlayer()
          )}
        </div>
      </div>
    );
  }
}

SpotReportCam.defaultProps = {
  spot: null,
  isPremium: false,
  adblock: false,
  userDetails: null,
  userSettings: null,
  reportData: null,
  playerId: 'sl-spot-report-camera',
  isMultiCam: false,
  multiCamTimeout: false,
  isPrimaryCam: false,
  timeoutMultiCams: () => null,
  resumeMultiCams: () => null,
  messageType: 'MSG',
  adFreePopupText: null,
};

SpotReportCam.propTypes = {
  isPremium: PropTypes.bool,
  spot: PropTypes.shape({
    name: PropTypes.string,
    subregion: PropTypes.shape({
      _id: PropTypes.string,
      forecastStatus: PropTypes.string,
    }),
    breadcrumb: PropTypes.array,
    cameras: PropTypes.array,
  }),
  camera: cameraPropType.isRequired,
  spotId: PropTypes.string.isRequired,
  legacySpotId: PropTypes.string.isRequired,
  adblock: PropTypes.bool,
  doClickedSubscribeCTA: PropTypes.func.isRequired,
  doTrackEvent: PropTypes.func.isRequired,
  advertisingIds: PropTypes.shape({
    spotId: PropTypes.string,
    subregionId: PropTypes.string,
  }).isRequired,
  isMultiCam: PropTypes.bool,
  userDetails: userDetailsPropTypes,
  userSettings: userSettingsPropTypes,
  reportData: spotReportDataPropType,
  playerId: PropTypes.string,
  multiCamTimeout: PropTypes.bool,
  isPrimaryCam: PropTypes.bool,
  timeoutMultiCams: PropTypes.func,
  resumeMultiCams: PropTypes.func,
  messageType: PropTypes.string,
  reportAssociated: PropTypes.shape({
    utcOffset: PropTypes.number,
  }).isRequired,
  adFreePopupText: PropTypes.shape({
    accept: PropTypes.string,
    decline: PropTypes.string,
    message: PropTypes.string,
    headline: PropTypes.string,
  }),
};

export default connect(
  state => ({
    advertisingIds: getAdvertisingIds(state),
    nearbySpots: getNearbySpots(state),
    nearbySpotsError: !!state.spot.nearby.error,
    reverseSortBeachView: getReverseSortBeachView(state),
    userSettings: getUserSettings(state),
    camListScrollPosition: getCamListScrollPosition(state),
    adblock: state.adblock,
    reportAssociated: getSpotReportAssociated(state),
  }),
  dispatch => ({
    doClickedSubscribeCTA: properties => dispatch(clickedSubscribeCTA(properties)),
    doTrackEvent: (event, properties) => dispatch(trackEvent(event, properties)),
  })
)(SpotReportCam);
